/**
 * Changing someone's address — two different operations behind one form.
 *
 * For a pending invitation it is a correction: nobody has used the address, so
 * the record moves at once and a fresh link goes out. For an accepted member it
 * is a credential change, which cannot take effect on an administrator's say-so
 * — the person has to confirm the new mailbox first, and the old address is
 * told either way. The dialog states which of the two is about to happen,
 * because the consequences are not the same and the button looks identical.
 */

import { useState } from 'react';
import { Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { describeError } from '@/lib/errorMessage';
import { useChangeMemberEmail, useUpdateInviteEmail, type PersonView } from '../api';

export function EditEmailDialog({
  person,
  mode,
  open,
  onOpenChange,
}: {
  person: PersonView;
  /** `invite` moves the record now; `member` starts a change they must confirm. */
  mode: 'invite' | 'member';
  open: boolean;
  onOpenChange: (value: boolean) => void;
}) {
  const updateInvite = useUpdateInviteEmail();
  const changeMember = useChangeMemberEmail();
  const [email, setEmail] = useState(person.email);
  const [error, setError] = useState<string | null>(null);

  const pending = updateInvite.isPending || changeMember.isPending;
  const unchanged = email.trim().toLowerCase() === person.email.toLowerCase();

  async function submit() {
    setError(null);
    const next = email.trim().toLowerCase();
    try {
      if (mode === 'invite') {
        await updateInvite.mutateAsync({ userId: person._id, email: next });
        toast.success(`Invitation moved to ${next} — a new link is on its way`);
      } else {
        await changeMember.mutateAsync({ userId: person._id, email: next });
        toast.success(`${next} must be confirmed before it becomes their sign-in`, {
          duration: 10_000,
        });
      }
      onOpenChange(false);
    } catch (err) {
      setError(describeError(err, 'The address could not be changed'));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle className="text-[16px] font-semibold">
          {mode === 'invite' ? 'Fix the invitation address' : `Change ${person.name}’s email`}
        </DialogTitle>
        <p className="mb-4 mt-0.5 text-[12px] vt-muted" data-testid="email-dialog-explainer">
          {mode === 'invite'
            ? 'Nobody has accepted yet, so this simply moves the invitation. The link already sent stops working and a new one goes out.'
            : 'They must confirm the new address before it becomes the one they sign in with. Both the old and the new address are told about this change.'}
        </p>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="member-email">Email</Label>
            <Input
              id="member-email"
              data-testid="member-email-input"
              type="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {error && (
            <p
              role="alert"
              data-testid="email-dialog-error"
              className="rounded-[6px] bg-[var(--danger)]/10 px-2.5 py-2 text-[12px] text-[var(--danger)]"
            >
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              data-testid="save-member-email"
              onClick={() => void submit()}
              disabled={pending || unchanged || !email.includes('@')}
            >
              {pending ? <Loader2 className="animate-spin" /> : <Mail />}
              {mode === 'invite' ? 'Save and resend' : 'Send confirmation'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
